import classnames from "classnames";
import { Caret } from "modules/common/components/collapse/Caret";
import { TextLink } from "modules/common/components/routing/TextLink";
import { columnLeft, columnsWidth, PlanGrid } from "modules/spending/workload-plan/models/plan-scale";
import React, { useCallback } from "react";
import { PlanLinks } from "../models/plan-links";
import { PlanOperationInput } from "../models/plan-draft";
import { ConflictRange } from "../models/plan-conflicts";
import { ProjectPaint, ProjectPlanSectionView } from "../models/project-plan-types";
import { columnAt } from "modules/spending/workload-plan/components/plan-geometry";
import { DraggableBar } from "./DraggableBar";
import { Menu, MenuItem } from "@blueprintjs/core";
import { showContextMenu } from "modules/common/services/context-menu";
import { ShiftFromTarget } from "./ShiftFromDialog";
import { ConflictZones, PlanTrack, trackStyles } from "./PlanTrack";
import styles from "./ProjectPlanSectionRow.module.scss";

/** Уже этой ширины шифр в полосу не влезает */
const LABEL_MIN_WIDTH = 26;

/** Ниже этой ширины подпись рисуется компактно */
const LABEL_TIGHT_WIDTH = 44;

interface ProjectPlanSectionRowProps {
    section: ProjectPlanSectionView;
    orderId: string;
    paint: ProjectPaint;
    grid: PlanGrid;
    monthBreaks: number[];
    selectedSlot: { start: number; length: number } | null;
    links: PlanLinks;
    collapsed: boolean;
    onToggle: (key: string) => void;
    /** Ключ раскрытия раздела в общем сворачивателе страницы */
    collapseKey: string;
    /** Режим правки и право на сроки проекта (все проекты или ГИП) */
    editable?: boolean;
    /** Даты колонок сетки: край срока встаёт на дату колонки */
    days?: string[];
    onOperation?: (operation: PlanOperationInput) => Promise<unknown> | void;
    /** Правый клик по полосе раздела в режиме правки — «Сдвинуть с даты» */
    onShiftFrom?: (target: ShiftFromTarget) => void;
    /** Дни конфликтов строки — подсветка поверх полос */
    conflicts?: ConflictRange[];
    /** Окно связей раздела */
    onOpenLinks?: (orderId: string, contentGuid: string, title: string) => void;
    /** От полосы раздела можно протягивать связи (точки у концов рисует LinksLayer) */
    linkConnectable?: boolean;
}

/**
 * Раздел состава проекта (режим «Разделы»): «Шифр — Описание», под ним ответственный
 * или подрядчик; полоса — от первого до последнего дня исполнителей. Аутсорс-раздел
 * со штриховкой, как договор подрядчика. Клик по строке раскрывает исполнителей,
 * клик по шифру ведёт в «Состав проекта».
 */
export const ProjectPlanSectionRow: React.FC<ProjectPlanSectionRowProps> = React.memo((props) => {
    const {
        section,
        orderId,
        paint,
        grid,
        monthBreaks,
        selectedSlot,
        links,
        collapsed,
        onToggle,
        collapseKey,
        editable,
        days,
        onOperation,
        onShiftFrom,
        conflicts,
        onOpenLinks,
        linkConnectable,
    } = props;

    const toggle = useCallback(() => onToggle(collapseKey), [onToggle, collapseKey]);

    const none = !section.id;
    const title = none ? section.name : section.code || section.name;
    const expandable = section.rows.length > 0;

    const tip = none ? section.name : `${section.name} — ${section.description}`;
    const tipNote = [
        section.note,
        section.outsourced ? "Раздел передан подрядчику" : "",
        section.inactive ? "Раздел удалён из состава проекта" : "",
    ]
        .filter(Boolean)
        .join(" · ");

    // срок раздела: заданный — по нему, иначе равен работам
    const summary = section.term || section.summary;

    // полосу подрядчика задаёт договор — её график не двигает
    const draggable = !!editable && !!onOperation && !!days && !!summary && !section.outsourced;

    /** Правый клик по строке раздела: его связи и «Сдвинуть с даты» (если можно править) */
    const openMenu = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault();

            const column = columnAt(event.currentTarget, event.clientX, grid);
            const none = !section.id;
            const title = none ? section.name : section.code || section.name;

            showContextMenu({
                targetOffset: { left: event.clientX, top: event.clientY },
                content: (
                    <Menu>
                        {!none && onOpenLinks && (
                            <MenuItem
                                text="Связи раздела…"
                                onClick={() => onOpenLinks(orderId, section.id, `${title}`)}
                            />
                        )}
                        {draggable && onShiftFrom && days && (
                            <MenuItem
                                text="Сдвинуть с даты…"
                                onClick={() =>
                                    onShiftFrom({
                                        kind: "section",
                                        orderId,
                                        contentGuid: section.id || null,
                                        title,
                                        day: days[Math.max(0, column)] || days[0],
                                    })
                                }
                            />
                        )}
                    </Menu>
                ),
            });
        },
        [grid, section.id, section.name, section.code, onOpenLinks, orderId, draggable, onShiftFrom, days]
    );

    const commit = useCallback(
        (mode: "move" | "start" | "end", delta: number) => {
            if (!onOperation || !days || !summary) {
                return;
            }

            const contentGuid = section.id || null;

            if (mode === "move") {
                return onOperation({ kind: "section", orderId, contentGuid, shift: delta });
            }

            const column = mode === "end" ? summary.start + summary.length - 1 + delta : summary.start + delta;
            const day = days[Math.max(0, Math.min(column, days.length - 1))];

            return onOperation({ kind: mode === "end" ? "section-end" : "section-start", orderId, contentGuid, day });
        },
        [onOperation, days, summary, section.id, orderId]
    );

    return (
        <div
            className={classnames(styles.row, { [styles.expandable]: expandable })}
            // строка раздела — куда ведёт стрелка связи, если работы раздела свёрнуты
            data-link-section={`${orderId}/${section.id || ""}`}
            onClick={expandable ? toggle : undefined}
        >
            <div className={styles.name} data-tip={tip} data-tip-note={tipNote}>
                {expandable ? <Caret collapsed={collapsed} /> : <span className={styles.caretStub} />}

                <div className={styles.info}>
                    <div className={styles.title}>
                        <TextLink to={none ? null : links.section(orderId, section.id)} className={styles.code}>
                            {title}
                        </TextLink>
                        {!none && section.description && (
                            <span className={styles.description}> — {section.description}</span>
                        )}
                    </div>

                    {section.note && <div className={styles.note}>{section.note}</div>}
                </div>
            </div>

            <PlanTrack
                grid={grid}
                monthBreaks={monthBreaks}
                selectedSlot={selectedSlot}
                className={styles.track}
                conflicts={conflicts}
                onContextMenu={section.id || (draggable && onShiftFrom && days) ? openMenu : undefined}
            >
                {section.parts.map((part) => {
                    const partWidth = columnsWidth(grid, part.start, part.length);
                    const partClass = classnames(trackStyles.block, {
                        [trackStyles.blockOutsourced]: section.outsourced,
                        [trackStyles.blockTight]: partWidth < LABEL_TIGHT_WIDTH,
                        [trackStyles.blockDark]: paint.dark,
                        [trackStyles.blockLight]: !paint.dark,
                        [trackStyles.cutLeft]: part.continuesLeft,
                        [trackStyles.cutRight]: part.continuesRight,
                        // прошлое бледное и не двигается — как у работ и в Табеле План
                        [trackStyles.blockPast]: part.isPast,
                    });
                    const partStyle = { backgroundColor: paint.background, borderColor: paint.borderColor };
                    const partCaption = (
                        <>
                            {partWidth >= LABEL_MIN_WIDTH && !none && (
                                <span className={trackStyles.blockLabel}>{section.code}</span>
                            )}
                            <ConflictZones grid={grid} start={part.start} length={part.length} conflicts={conflicts} />
                        </>
                    );

                    if (draggable && !part.isPast) {
                        return (
                            <DraggableBar
                                key="current"
                                grid={grid}
                                start={part.start}
                                length={part.length}
                                enabled={true}
                                // у строки «Без раздела» срока нет — её можно только сдвинуть целиком;
                                // левый край срока, начавшегося в прошлом, — сегодня, его не тянут
                                edges={{
                                    start: !none && !part.afterPast && !part.continuesLeft,
                                    end: !none && !part.continuesRight,
                                }}
                                className={partClass}
                                style={partStyle}
                                tip={tip}
                                tipNote={tipNote}
                                linkEnd={none ? undefined : `section:${orderId}/${section.id}`}
                                linkConnectable={!!linkConnectable && !none}
                                onCommit={commit}
                            >
                                {partCaption}
                            </DraggableBar>
                        );
                    }

                    return (
                        <div
                            key={part.isPast ? "past" : "current"}
                            className={partClass}
                            style={{ ...partStyle, left: columnLeft(grid, part.start), width: partWidth }}
                            data-link-end={none ? undefined : `section:${orderId}/${section.id}`}
                            data-tip={tip}
                            data-tip-note={tipNote}
                        >
                            {partCaption}
                        </div>
                    );
                })}
            </PlanTrack>
        </div>
    );
});

ProjectPlanSectionRow.displayName = "ProjectPlanSectionRow";

/** Подзаголовок стадии над её разделами: «Проектная документация» */
export const ProjectPlanStageCaption: React.FC<{ stage: string; width: number }> = ({ stage, width }) => (
    <div className={styles.stage} style={{ minWidth: width }}>
        <div className={styles.stageName}>{stage}</div>
    </div>
);
